import React, { useContext, useMemo, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LoginIcon from "@mui/icons-material/Login";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../auth/auth-context";
import AccountMenu from "./accountMenu";
import { PlanyBrandMark } from "./planyBrandMark";

const MOBILE_TOOLBAR_H = 64;
const DESKTOP_TOOLBAR_H = 74;
const PLANY_ASSIST_PHONE = "tel:+18333681399";

const navButtonSx = {
  whiteSpace: "nowrap",
  borderRadius: 999,
  px: 1.6,
  py: 0.85,
  textTransform: "none",
  fontWeight: 700,
  letterSpacing: "0.01em",
  color: "rgba(244,249,252,0.92)",
  borderColor: "rgba(255,255,255,0.18)",
  "&:hover": {
    borderColor: "rgba(255,255,255,0.28)",
    backgroundColor: "rgba(255,255,255,0.08)",
  },
};

const navItems = [
  {
    label: "Refer a Client",
    to: "/referrals",
    icon: <AssignmentRoundedIcon fontSize="small" />,
    match: (pathname) => pathname === "/referrals",
  },
  {
    label: "Referral Portal",
    to: "/referrals/portal",
    icon: <DashboardRoundedIcon fontSize="small" />,
    match: (pathname) => pathname.startsWith("/referrals/portal"),
  },
];

export default function ReferrerHeader() {
  const { providerUser, logout } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthed = Boolean(providerUser?.userId);
  const loginHref = useMemo(
    () =>
      `/login?returnTo=${encodeURIComponent(
        `${location.pathname}${location.search}${location.hash}`
      )}`,
    [location.pathname, location.search, location.hash]
  );
  const signupHref = useMemo(
    () =>
      `/signup?returnTo=${encodeURIComponent(
        `${location.pathname}${location.search}${location.hash}`
      )}`,
    [location.pathname, location.search, location.hash]
  );

  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);

  const handleLogout = () => {
    try {
      logout?.();
    } finally {
      navigate("/");
      window.location.reload();
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        background:
          "linear-gradient(90deg, rgba(11,70,78,0.98) 0%, rgba(20,50,74,0.98) 100%)",
        boxShadow: "0 18px 40px rgba(8,22,34,0.18)",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: { xs: MOBILE_TOOLBAR_H, sm: DESKTOP_TOOLBAR_H },
          px: { xs: 1.25, sm: 2.25 },
          gap: { xs: 1, sm: 2 },
        }}
      >
        <Box
          component={RouterLink}
          to="/referrals"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: { xs: 1, sm: 1.25 },
              py: 0.75,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.14)",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="span"
                sx={{
                  color: "#ffffff",
                  fontWeight: 700,
                  letterSpacing: "0.01em",
                  fontSize: { xs: "0.88rem", sm: "0.98rem" },
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                <PlanyBrandMark />
              </Typography>
            </Box>
            <Chip
              label="Partner"
              size="small"
              sx={{
                height: 24,
                display: { xs: "none", sm: "inline-flex" },
                bgcolor: "rgba(223,247,247,0.92)",
                color: "#0d5b66",
                fontWeight: 800,
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1,
            ml: 1,
          }}
        >
          {navItems.map((item) => {
            const active = item.match(location.pathname);
            return (
              <Button
                key={item.to}
                component={RouterLink}
                to={item.to}
                variant={active ? "contained" : "outlined"}
                startIcon={item.icon}
                sx={{
                  ...navButtonSx,
                  bgcolor: active ? "rgba(255,255,255,0.14)" : "transparent",
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Box>

        <Box
          sx={{
            ml: "auto",
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1,
          }}
        >
          <Button
            component="a"
            href={PLANY_ASSIST_PHONE}
            variant="outlined"
            startIcon={<SupportAgentOutlinedIcon fontSize="small" />}
            sx={navButtonSx}
          >
            Call Plany Assist
          </Button>

          {isAuthed ? (
            <AccountMenu showNotification={false} />
          ) : (
            <React.Fragment>
              <Button
                component={RouterLink}
                to={loginHref}
                variant="outlined"
                startIcon={<LoginIcon fontSize="small" />}
                sx={navButtonSx}
              >
                Log In
              </Button>
              <Button
                component={RouterLink}
                to={signupHref}
                variant="contained"
                sx={{
                  ...navButtonSx,
                  bgcolor: "rgba(255,255,255,0.16)",
                }}
              >
                Create Account
              </Button>
            </React.Fragment>
          )}
        </Box>

        <IconButton
          color="inherit"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{
            ml: "auto",
            display: { xs: "inline-flex", md: "none" },
            bgcolor: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <MoreVertIcon />
        </IconButton>

        <Menu
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              minWidth: 230,
              borderRadius: 3,
              mt: 1,
              p: 0.5,
            },
          }}
        >
          {navItems.map((item) => (
            <MenuItem
              key={item.to}
              component={RouterLink}
              to={item.to}
              onClick={() => setMenuAnchor(null)}
            >
              {item.label}
            </MenuItem>
          ))}
          <MenuItem
            component="a"
            href={PLANY_ASSIST_PHONE}
            onClick={() => setMenuAnchor(null)}
          >
            <SupportAgentOutlinedIcon fontSize="small" style={{ marginRight: 10 }} />
            Call Plany Assist
          </MenuItem>

          {isAuthed ? (
            <React.Fragment>
              <MenuItem
                component={RouterLink}
                to="/profile"
                onClick={() => setMenuAnchor(null)}
              >
                <PersonOutlineRoundedIcon fontSize="small" style={{ marginRight: 10 }} />
                Profile
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  handleLogout();
                }}
              >
                <LogoutRoundedIcon fontSize="small" style={{ marginRight: 10 }} />
                Log Out
              </MenuItem>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <MenuItem
                component={RouterLink}
                to={loginHref}
                onClick={() => setMenuAnchor(null)}
              >
                <LoginIcon fontSize="small" style={{ marginRight: 10 }} />
                Log In
              </MenuItem>
              <MenuItem
                component={RouterLink}
                to={signupHref}
                onClick={() => setMenuAnchor(null)}
              >
                Create Account
              </MenuItem>
            </React.Fragment>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
